import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { supportApi } from "../client.js";
import type { ConversationDetail, EscalateRequest } from "../types.js";

/**
 * Modal that turns a conversation into a Ticket. Pre-fills the subject
 * from the first user message; status and priority dropdowns hydrate
 * from the existing /api/support/{statuses,priorities} endpoints. The
 * caller owns navigation after success — we just resolve the API call.
 */
export function EscalateModal({
  conversation,
  onCancel,
  onConfirm,
}: {
  conversation: ConversationDetail;
  onCancel: () => void;
  onConfirm: (body: EscalateRequest) => Promise<void>;
}): JSX.Element {
  const statusesQuery   = useQuery({ queryKey: ["support", "statuses"],   queryFn: supportApi.listStatuses });
  const prioritiesQuery = useQuery({ queryKey: ["support", "priorities"], queryFn: supportApi.listPriorities });

  const firstUser = conversation.messages.find((m) => m.role === "user");
  const [subject, setSubject]     = useState((firstUser?.content ?? "").slice(0, 80));
  const [statusId, setStatusId]   = useState<string>("");
  const [priorityId, setPriorityId] = useState<string>("");
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (!subject.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body: EscalateRequest = { subject: subject.trim() };
      if (statusId) body.ticket_status_id = Number(statusId);
      if (priorityId) body.ticket_priority_id = Number(priorityId);
      await onConfirm(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => { if (!busy) onCancel(); }}
      maxWidth="sm"
      fullWidth
      aria-label="Escalate conversation to ticket"
    >
      <DialogTitle sx={{ fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
        Escalate to ticket
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14, color: "text.disabled" }}>
          Creates a new ticket with the full transcript as the
          description, attributed to the customer who chatted with Otto.
        </DialogContentText>

        <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
          <TextField
            label="Subject"
            inputProps={{ "aria-label": "Subject", maxLength: 200 }}
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            fullWidth
          />

          <TextField
            select
            label="Status (optional — defaults to the org's initial status)"
            inputProps={{ "aria-label": "Status" }}
            value={statusId}
            onChange={(e) => setStatusId(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Default</MenuItem>
            {(statusesQuery.data?.data ?? []).map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Priority (optional — defaults to the org's normal priority)"
            inputProps={{ "aria-label": "Priority" }}
            value={priorityId}
            onChange={(e) => setPriorityId(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Default</MenuItem>
            {(prioritiesQuery.data?.data ?? []).map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
            ))}
          </TextField>
        </Box>

        {error && (
          <Typography
            sx={{
              mt: 1.5,
              borderRadius: 1,
              border: 1,
              borderColor: (t) => alpha(t.palette.error.main, 0.4),
              bgcolor: (t) => alpha(t.palette.error.main, 0.05),
              px: 1.5,
              py: 1,
              fontSize: 12,
              color: "error.main",
            }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || !subject.trim()}>
          {busy ? "Creating ticket…" : "Create ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
